import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, IconButton, InputAdornment, TextField, Typography, Grid, Stack, FormControl, InputLabel, Select, OutlinedInput, Chip, MenuItem } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { useNavigate } from 'react-router-dom';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import { deleteLegal, getLegalList, openSnackbar } from '../../redux/slices/common';
import { CloseRounded } from '@mui/icons-material';
import SearchIcon from 'common/customIcons/search';
import CommonLayout from 'layouts/DashboardLayout';
import MDButton from 'components/MDButton';
import { formatTimestampToWithoutTime } from 'utilities/common';
import { useTheme } from '@emotion/react';
import { RemoveRedEye } from '@mui/icons-material';
import { formatTimestampToDateView } from 'utilities/common';
import DeleteIcon from 'common/customIcons/delete';
import { getData } from 'utilities/common';
import { getPermissionsByMode } from 'utilities/common';
import { hasSectionPermission } from 'utilities/common';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const names = [
    'Hemp',
    'Hyperwolf',
    'Stilo'
];

function getStyles(name, personName, theme) {
    return {
        fontWeight: personName.includes(name)
            ? theme.typography.fontWeightMedium
            : theme.typography.fontWeightRegular,
    };
}

export default function IndexLegal() {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const productsRef = useRef([]); // Ref to store products data
    const pageRef = useRef(0);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const [personName, setPersonName] = React.useState([]);
    const gridRefApi = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { loading = false, legalList = {} } = useSelector(state => state.common);
    const dataPassRef = useRef([]);

    let localUser = getData('login-user-info')
    if (localUser) {
        localUser = JSON.parse(localUser)
    }

    const adminMode = localStorage.getItem("adminsMode") ? JSON.parse(localStorage.getItem("adminsMode")) : null;
    const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);

    const canCreate = hasSectionPermission(permissionsForMode, "Manage Website", "Create");
    const canEdit = hasSectionPermission(permissionsForMode, "Manage Website", "Edit/Update");
    const canView = hasSectionPermission(permissionsForMode, "Manage Website", "View Only");
    const canDelete = hasSectionPermission(permissionsForMode, "Manage Website", "Delete");


    const deleteHandler = (cannabinoidId) => {
        dispatch(setModalLoader(true));
        dispatch(deleteLegal(cannabinoidId)).unwrap().then((res) => {
            showResponseMessage(res?.message, 'success');
            dispatch(closeCustomModalDialog());
            pageRef.current = 0;
            const dataSource = {
                getRows
            };
            mainGridApiRef.current.ensureIndexVisible(0, null);
            mainGridApiRef.current.setGridOption('datasource', dataSource);
        }).catch((err) => {
            dispatch(setModalLoader(false));
            showResponseMessage(err?.message, 'error');
        });
    };

    const showResponseMessage = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };
    //***************************************  select handler ***************************************
    // const handleChange = (event) => {
    //     const {
    //         target: { value },
    //     } = event;
    //     setPersonName(
    //         typeof value === 'string' ? value.split(',') : value,
    //     );
    //     searchFn(value);
    // };

    const handleChange = (event) => {
        const {
            target: { value },
        } = event;

        // Capitalize the first letter of each value
        const capitalizedValues = typeof value === 'string'
            ? value.split(',').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            : value.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

        setPersonName(capitalizedValues); // Store capitalized values in state

        const lowerCaseSearchValue = capitalizedValues.map(v => v.toLowerCase()); // Convert to lowercase for searching
        searchFn(lowerCaseSearchValue); // Pass lowercase values for search
    };

    const columnDefs = [
        {
            headerName: 'Page Name',
            field: 'heading',
            // sortable: false,
            suppressMenu: false,
            minWidth: 100,
            flex: 1,
            cellRenderer: (props) => {
                return props?.data?.heading ? props?.data?.heading : "-"
            },
            cellStyle: { whiteSpace: 'normal', lineHeight: '1.5' }
        },
        {
            headerName: 'Platform',
            // sortable: false,
            field: 'platform',
            // field: 'platformAvailability',

            suppressMenu: false,
            minWidth: 100,
            flex: 1,
            cellStyle: { whiteSpace: 'normal', lineHeight: '1.5' },
            cellRenderer: (props) => {
                const platformData = props?.data?.platformAvailability;

                if (!platformData) return "-";

                // If "all" is true, return "All"
                if (platformData.all) return <span
                    className="tab"

                // className="platform-badge platform-badge-all"
                >All</span>;

                // Filter out keys with `true` values and render them as separate elements
                const activePlatforms = Object.keys(platformData)
                    .filter(key => platformData[key] && key !== "all")
                    .map((platform, index) => (
                        <span key={index}
                            className="tab"
                        // className="platform-badge"
                        >
                            {platform}
                        </span>
                    ));

                return activePlatforms.length > 0 ? activePlatforms : "-";
            }

        },
        {
            headerName: 'Updated By',
            field: 'createdBy',
            suppressMenu: false,
            minWidth: 100,
            sortable: false,
            flex: 1,
            cellStyle: { whiteSpace: 'normal', lineHeight: '1.5' },
            cellRenderer: (props) => {
                return props?.value ? props?.value : "-"
            },
        },
        {
            headerName: 'Updated Date',
            field: 'updatedDate',
            suppressMenu: false,
            // sortable: false,
            minWidth: 100,
            flex: 1,
            cellStyle: { whiteSpace: 'normal', lineHeight: '1.5' },
            cellRenderer: useCallback((props) => (
                props?.data?.updatedDate ? formatTimestampToDateView(props?.data?.updatedDate) : '-'
            ), []),
        },
        {
            headerName: 'Actions',
            sortable: false,
            cellRenderer: (props) => {
                if (!props?.data?._id) return;
                return <Box display="inline-flex" gap={1} alignItems="center">
                    <IconButton
                        disabled={!canView ? true : false}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/manages/static/details/${props?.data?.sectionType}?roleTab=hemp&heading=${props?.data?.heading}&slug=${props?.data?.slug}`)
                        }} color='primary' size='small' variant="primary" sx={{ marginLeft: 1 }}>
                        <RemoveRedEye />
                    </IconButton>
                    <IconButton
                        disabled={!canEdit ? true : false}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEditClicked(props);
                        }} color='primary' size='small' variant="primary">
                        <EditIcon width='16px' height='16px' />
                    </IconButton>
                    {/* <IconButton color='error' size='small' variant='primary' sx={{ marginLeft: 1 }}
                        onClick={(e) => {
                            e.stopPropagation()
                            dispatch(openCustomModalDialog({ modalData: { deleteHandler, deleteId: props?.data?.slug, deleteType: 'Page' }, modal: 'DELETE_MODAL' }))
                        }}  >
                        <DeleteIcon width='16px' height='16px' />
                    </IconButton> */}
                </Box>;
            },
            cellStyle: { textAlign: 'right', justifyContent: 'end' },
            headerClass: 'right-aligned-cell-header',
            minWidth: 100,
            flex: 1,
            pinned: 'right'
        },
    ];

    function getSortInfo(data) {
        const sortInfo = {
            sortBy: '',
            sortOrder: ''
        };
        data.forEach(item => {
            sortInfo.sortBy = item.colId;
            sortInfo.sortOrder = item.sort === "asc" ? 'asc' : 'desc';
        });
        return sortInfo;
    }

    const getRows = async (params, value) => {
        gridRefApi.current = params;
        let filter = {};
        if (params?.sortModel.length) {
            filter = { ...getSortInfo(params?.sortModel) };
        }


        // Make the API call for other columns (if the sorting is not by 'status')
        dispatch(getLegalList({
            skip: 0,
            limit: 20,
            searchTerm: searchTermRef.current,
            ...(Array.isArray(value) && value.length > 0 ? { platform: value.join(",") } : {}),
            ...filter
        }))
            .unwrap().then(res => {
                const { legal = [], total } = res;
                productsRef.current = legal;

                // Handle the status on the frontend side if it's not 'status' column sorting
                const cannabinoidsArray = legal.map((item) => {
                    return {
                        ...item,
                        status: item?.status ? 'active' : 'inactive' // Modify the status based on the backend response
                    };
                });

                // Pass the modified data to the grid
                params.successCallback(cannabinoidsArray, total);

                // Update the page reference
                pageRef.current = pageRef.current + 20;
            }).catch(() => {
                params.successCallback([], 0);
            });
    };

    const searchFn = (value) => {
        const dataSource = {
            getRows: (params) => getRows(params, value) // Pass the current filter payload
        };
        mainGridApiRef.current.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption('datasource', dataSource);
    };

    const onEditClicked = (row) => {
        navigate(`/manages/static/edit/${row?.data?.sectionType}?roleTab=hemp&heading=${row?.data?.heading}&slug=${row?.data?.slug}`);
    };

    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (legalList?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [legalList, loading]);

    const handleSearch = useDebounce(() => {
        pageRef.current = 0;
        searchFn();
    }, 500);

    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
    };

    const onRowClicked = useCallback((row) => {
        if (row?.colDef?.headerName !== 'Actions') {
            navigate(`/manages/static/details/${row?.data?.sectionType}?roleTab=hemp&heading=${row?.data?.heading}&slug=${row?.data?.slug}`)
        }
    }, []);

    return (
        <CommonLayout>
            {

                <Box p={{ xs: 2, sm: 3, lg: 4 }} className="row-cursor">
                    <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
                        <Grid item xs={12} sm={3}>
                            <Typography variant='h4'>Static Pages</Typography>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>
                                <Box sx={{ width: 300 }}>
                                    <TextField fullWidth className="custom-search-field" placeholder="Search" label="" value={searchTerm} onChange={(e) => searchHandler(e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">
                                                <IconButton size='small'>
                                                    <SearchIcon width='18px' height='18px' />
                                                </IconButton>
                                            </InputAdornment>,
                                            endAdornment: searchTerm?.length > 0 ? <InputAdornment>
                                                <IconButton onClick={() => searchHandler('')} size='small'>
                                                    <CloseRounded />
                                                </IconButton>
                                            </InputAdornment> : null
                                        }}
                                    />
                                </Box>

                                <FormControl sx={{ width: 300 }} className='multi-select-platform'>
                                    <InputLabel id="demo-multiple-chip-label">Select Platform</InputLabel>
                                    <Select
                                        className='form-select'
                                        labelId="demo-multiple-chip-label"
                                        id="demo-multiple-chip"
                                        multiple
                                        value={personName}
                                        onChange={handleChange}
                                        input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => (
                                                    <Chip key={value} label={value} />
                                                ))}
                                            </Box>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        {names?.map((name) => (
                                            <MenuItem
                                                key={name}
                                                value={name}
                                                // disabled={name === 'Stilo'} // Disable 'Stilo'
                                                style={getStyles(name, personName, theme)}
                                            >
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {
                                    canCreate &&
                                    <MDButton
                                        sx={{ minWidth: '160px' }}
                                        type='button'
                                        variant='contained'
                                        color='primary'
                                        onClick={() => {
                                            dispatch(openCustomModalDialog({
                                                modal: 'STATIC_TEMPLATE_ADD',
                                                modalData: ""
                                            }))
                                        }}
                                    // onClick={() => { navigate(`/manages/static/add?template=about_us_template&roleTab=hemp`); }}
                                    >
                                        {'+ Add'}
                                    </MDButton>
                                }

                                {/* <MDButton 
                                sx={{ minWidth: '140px' }}
                                type='button'
                                variant='contained'
                                color='primary'
                                onClick={()=>{navigate(`/manages/static/${null}`)}}
                            >
                                {'Create Legal Page'}
                            </MDButton> */}
                            </Stack>
                        </Grid>
                    </Grid>
                    {
                        canView &&
                        <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef} isLoading={loading} style={{ height: '70vh' }} />
                    }
                </Box>
            }
        </CommonLayout>
    );
}